import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { API_BASE } from '@/config/api'

export interface CryptoResult {
  symbol: string
  rsr: number
  volume: {
    rvol_m5?: number
    rvol_h1?: number
    rvol_day?: number
  }
}

export const useCryptoStore = defineStore('crypto', () => {
  // -------------------------
  // LocalStorage helpers
  // -------------------------
  const load = (key: string, def: string) => {
    const v = localStorage.getItem(key)
    return v ?? def
  }

  const t1_input = ref(load('t1_input', '19/12/2025 14:20'))
  const t2_input = ref(load('t2_input', '19/12/2025 15:20'))
  const useNow = ref(localStorage.getItem('useNow') === 'true')

  const results = ref<CryptoResult[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const refreshMinutes = ref(0)
  let timer: number | null = null

  // -------------------------
  // Persist settings
  // -------------------------
  watch(t1_input, v => localStorage.setItem('t1_input', v))
  watch(t2_input, v => localStorage.setItem('t2_input', v))
  watch(useNow, v => localStorage.setItem('useNow', v ? 'true' : 'false'))

  // -------------------------
  // Fetch RSR
  // -------------------------
  async function fetchRSR() {
    try {
      isLoading.value = true
      error.value = null

      const t1 = encodeURIComponent(t1_input.value)
      let url = ''

      if (useNow.value) {
        url = `${API_BASE}/rsr_latest?t1=${t1}`
      } else {
        const t2 = encodeURIComponent(t2_input.value)
        url = `${API_BASE}/rsr?t1=${t1}&t2=${t2}`
      }

      const res = await fetch(url)
      if (!res.ok) throw new Error('API Error')

      const json = await res.json()
      results.value = json.results ?? []
    } catch (e) {
      console.error(e)
      error.value = 'โหลดข้อมูลล้มเหลว'
    } finally {
      isLoading.value = false
    }
  }

  // -------------------------
  // Auto refresh
  // -------------------------
  function startAutoRefresh() {
    stopAutoRefresh()
    if (refreshMinutes.value > 0) {
      timer = window.setInterval(
        fetchRSR,
        refreshMinutes.value * 60 * 1000
      )
    }
  }

  function stopAutoRefresh() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  function setRefreshMinutes(min: number) {
    refreshMinutes.value = min
    startAutoRefresh()
  }

  return {
    results,
    isLoading,
    error,
    t1_input,
    t2_input,
    useNow,
    refreshMinutes,
    setRefreshMinutes,
    startAutoRefresh,
    stopAutoRefresh,
    fetchRSR,
  }
})
